<template>
  <section class="offer-band offer-last-minute">
    <div class="offer-heading">
      <div>
        <h2>DMDS : séjours tarifés · GetLastMinuteOffers</h2>
        <p class="offer-meta mb-0">
          Des séjours à dates fixes, avec hébergement, prix et contexte de réservation.
          Ils sont comptés séparément des définitions DMDS de GetSpecialOffers, sans rapprochement automatique entre les deux sources.
        </p>
      </div>
      <v-btn
        small
        outlined
        color="info"
        :loading="loading"
        @click="refresh"
      >
        Actualiser les séjours DMDS
      </v-btn>
    </div>
    <v-alert
      v-if="refreshError"
      type="error"
      outlined
      role="alert"
    >
      {{ refreshError }} Les données conservées peuvent ne plus correspondre aux filtres sélectionnés.
    </v-alert>
    <v-progress-linear
      v-if="loading && !inventory"
      indeterminate
      color="info"
      aria-label="Chargement des séjours DMDS"
    ></v-progress-linear>
    <div
      class="offer-metrics"
      aria-label="Statistiques GetLastMinuteOffers"
    >
      <div
        v-for="metric in metrics"
        :key="metric.label"
        class="offer-metric"
      >
        <div class="text-caption text-uppercase">
          {{ metric.label }}
        </div>
        <div class="text-h6 font-weight-bold offer-number">
          {{ formatNumber(metric.value) }}
        </div>
        <div class="offer-meta">
          {{ metric.hint }}
        </div>
      </div>
    </div>
    <template v-if="inventory">
      <div class="offer-splits">
        <section class="offer-band">
          <h3>Dates et prix enregistrés</h3>
          <dl class="offer-definition-list">
            <dt>Séjours commencés, non terminés</dt><dd>{{ formatNumber(inventory.currentCount) }}</dd>
            <dt>Séjours terminés</dt><dd>{{ formatNumber(inventory.pastCount) }}</dd>
            <dt>Échéance de publication dépassée</dt><dd>{{ formatNumber(inventory.publicationExpiredCount) }}</dd>
            <dt>Sans échéance de publication renseignée</dt><dd>{{ formatNumber(inventory.withoutDeadlineCount) }}</dd>
            <dt>Hébergements connus concernés</dt><dd>{{ formatNumber(inventory.accommodationCount) }}</dd>
            <dt>Prix promo minimum / maximum</dt><dd>{{ formatAmount(inventory.minPromoPrice) }} / {{ formatAmount(inventory.maxPromoPrice) }}</dd>
          </dl>
          <p class="offer-meta mb-0">
            Dates de séjour : {{ formatDay(inventory.firstStart) }} au {{ formatDay(inventory.lastEnd) }}.
            Le jour du départ est inclus dans les séjours à venir ; le jour de sortie marque un séjour terminé.
            L'échéance de publication est un indicateur séparé, pas une erreur technique.
          </p>
        </section>
        <section class="offer-band">
          <h3>Qualité et langues</h3>
          <dl class="offer-definition-list">
            <template v-for="metric in qualityMetrics">
              <dt :key="metric.label + '-label'">
                {{ metric.label }}
              </dt>
              <dd :key="metric.label + '-value'">
                {{ formatNumber(metric.value) }}
              </dd>
            </template>
          </dl>
          <div
            v-for="translation in inventory.translations"
            :key="translation.locale"
            class="offer-language"
          >
            <span>{{ translation.locale.toUpperCase() }}</span>
            <v-progress-linear
              :value="percentage(translation.stayCount, inventory.stayCount)"
              color="info"
              height="5"
              :aria-label="translation.locale + ' : ' + translation.stayCount + ' séjours avec libellé'"
            ></v-progress-linear>
            <span class="offer-number">{{ formatNumber(translation.stayCount) }} / {{ formatNumber(inventory.stayCount) }}</span>
          </div>
          <p class="offer-meta mb-0">
            Ces lignes n'ont pas de catégorie ou super-catégorie : leur absence n'est pas un point de contrôle ici.
          </p>
        </section>
      </div>
      <p class="offer-meta">
        Synthèse calculée le {{ formatDate(inventory.computedAt) }} (cache 60 s), référence {{ formatDay(inventory.referenceDate) }}.
        Ce n'est pas la date du dernier import. Les filtres ne modifient pas ces totaux globaux.
        Prix et jetons sont des instantanés, sans garantie de disponibilité actuelle.
        Stock, frais, devise et URL de réservation ne sont pas enregistrés dans cette table.
      </p>
    </template>
    <v-tabs
      v-model="tab"
      color="info"
      show-arrows
      class="mb-5"
    >
      <v-tab>Séjours DMDS</v-tab>
      <v-tab>Couverture par camping</v-tab>
    </v-tabs>
    <div v-show="tab === 0">
      <v-chip
        v-if="selectedCamping"
        close
        outlined
        color="info"
        class="mb-4"
        @click:close="clearCamping"
      >
        {{ selectedCamping.name || 'Camping' }} · SH {{ selectedCamping.establishmentId }}
      </v-chip>
      <form @submit.prevent="filtersChanged('offers')">
        <div class="offer-filters">
          <v-text-field
            v-model="search"
            label="Offre, camping, hébergement ou identifiant SH"
            outlined
            dense
            hide-details
            maxlength="120"
            clearable
          ></v-text-field>
          <v-select
            v-model="status"
            :items="statusOptions"
            label="Dates du séjour"
            outlined
            dense
            hide-details
            @change="filtersChanged('offers')"
          ></v-select>
          <v-select
            v-model="publication"
            :items="publicationOptions"
            label="Publication enregistrée"
            outlined
            dense
            hide-details
            @change="filtersChanged('offers')"
          ></v-select>
          <v-select
            v-model="issue"
            :items="issueOptions"
            label="Points de contrôle"
            outlined
            dense
            hide-details
            @change="filtersChanged('offers')"
          ></v-select>
        </div>
        <div class="offer-controls mb-5">
          <v-btn
            type="submit"
            color="info"
            outlined
            :disabled="loading"
          >
            Rechercher
          </v-btn>
          <v-btn
            text
            :disabled="loading"
            @click="resetOffers"
          >
            Réinitialiser les filtres
          </v-btn>
        </div>
      </form>
      <v-data-table
        :headers="offerHeaders"
        :items="offers"
        :loading="loading"
        :items-per-page="25"
        hide-default-footer
        disable-sort
        show-expand
        class="offer-table"
        no-data-text="Aucun séjour DMDS ne correspond à ces filtres."
        loading-text="Chargement des séjours DMDS..."
      >
        <template v-slot:item.name="{ item }">
          <strong class="offer-name">{{ item.name || 'Libellé FR absent' }}</strong>
          <div class="offer-meta">
            GetLastMinuteOffers · SH {{ item.sourceId || '-' }}
          </div>
        </template>
        <template v-slot:item.campingName="{ item }">
          <span class="offer-camping-name">{{ item.campingName || 'Camping non renseigné' }}</span>
          <div class="offer-meta">
            SH {{ item.establishmentId || '-' }}
          </div>
        </template>
        <template v-slot:item.accommodationName="{ item }">
          <span class="offer-name">{{ item.accommodationName || 'Hébergement non renseigné' }}</span>
          <div class="offer-meta">
            Produit SH {{ item.productId || '-' }}
          </div>
        </template>
        <template v-slot:item.start="{ item }">
          <div>{{ formatDay(item.start) }} au {{ formatDay(item.end) }}</div>
          <div class="offer-meta">
            {{ formatNumber(item.nights) }} nuits
          </div>
          <v-chip
            small
            label
            outlined
            :color="stayStatusColor(item.status)"
          >
            {{ stayStatusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.promoPrice="{ item }">
          <strong class="offer-number">{{ formatAmount(item.promoPrice) }}</strong>
          <div class="offer-meta">
            Initial : {{ formatAmount(item.basePrice) }}
          </div>
        </template>
        <template v-slot:item.issues="{ item }">
          <div
            v-for="code in item.issues"
            :key="code"
            class="warning--text offer-issue"
          >
            {{ issueLabel(code) }}
          </div>
          <span v-if="!item.issues.length">Aucun signal</span>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <div class="offer-expanded">
              <div class="offer-detail-summary">
                <div>
                  <h3>Contexte du séjour</h3>
                  <div>Personnes : {{ formatNumber(item.personMin) }} à {{ formatNumber(item.personMax) }}, {{ formatNumber(item.personIncluded) }} incluses</div>
                  <div>Jours avant départ : {{ formatNumber(item.daysToStart) }} (recalculés aujourd'hui)</div>
                  <div>Fin de publication : {{ formatDay(item.publicationEnd) }}</div>
                  <div
                    v-if="item.publicationExpired"
                    class="offer-meta"
                  >
                    Échéance dépassée
                  </div>
                  <div
                    v-else
                    class="offer-meta"
                  >
                    Échéance non dépassée ou non renseignée, sans garantie de disponibilité
                  </div>
                </div>
                <div>
                  <h3>Jetons de réservation enregistrés</h3>
                  <div class="offer-id">
                    TAC : {{ item.hasTac ? item.tac : 'Absent' }}
                  </div>
                  <div class="offer-id mt-2">
                    SPEC : {{ item.hasSpec ? item.spec : 'Absent' }}
                  </div>
                  <p class="offer-meta mb-0">
                    Conservés tels que reçus. Leur présence ne prouve pas qu'une réservation reste possible.
                  </p>
                </div>
              </div>
              <div class="offer-meta">
                Libellés : {{ item.locales.length ? item.locales.join(', ').toUpperCase() : 'Aucun' }}
              </div>
              <div class="offer-id">
                {{ item.id }}
              </div>
            </div>
          </td>
        </template>
      </v-data-table>
      <div class="offer-footer">
        <span class="offer-meta">{{ formatNumber(offerTotal) }} séjours tarifés · Une ligne par camping / idOffer, hors doublons historiques.</span>
        <v-pagination
          v-if="offerTotal > 25"
          v-model="offerPage"
          :length="Math.ceil(offerTotal / 25)"
          :total-visible="5"
          color="info"
          @input="refresh"
        ></v-pagination>
      </div>
    </div>
    <div v-show="tab === 1">
      <form
        class="offer-camping-filters"
        @submit.prevent="filtersChanged('campings')"
      >
        <v-text-field
          v-model="campingSearch"
          label="Nom du camping ou identifiant SH"
          outlined
          dense
          hide-details
          maxlength="120"
          clearable
        ></v-text-field>
        <v-select
          v-model="coverage"
          :items="coverageOptions"
          label="Couverture GetLastMinuteOffers"
          outlined
          dense
          hide-details
          @change="filtersChanged('campings')"
        ></v-select>
        <v-btn
          type="submit"
          color="info"
          outlined
          :disabled="loading"
        >
          Rechercher
        </v-btn>
      </form>
      <v-data-table
        :headers="campingHeaders"
        :items="campings"
        :loading="loading"
        :items-per-page="25"
        hide-default-footer
        disable-sort
        class="offer-table"
        no-data-text="Aucun camping ne correspond à ces filtres."
        loading-text="Chargement des campings..."
      >
        <template v-slot:item.name="{ item }">
          <span class="offer-camping-name">{{ item.name || 'Camping sans nom' }}</span>
          <div class="offer-meta">
            SH {{ item.establishmentId || '-' }} · {{ item.enabled ? 'Activé' : 'Désactivé' }}
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            small
            text
            color="info"
            :aria-label="'Voir les séjours DMDS de ' + (item.name || item.establishmentId)"
            @click="showCamping(item)"
          >
            Voir les séjours
          </v-btn>
        </template>
      </v-data-table>
      <div class="offer-footer">
        <span class="offer-meta">{{ formatNumber(campingTotal) }} campings suivis, y compris désactivés</span>
        <v-pagination
          v-if="campingTotal > 25"
          v-model="campingPage"
          :length="Math.ceil(campingTotal / 25)"
          :total-visible="5"
          color="info"
          @input="refresh"
        ></v-pagination>
      </div>
    </div>
    <p class="offer-meta mt-4 mb-0">
      Listes actualisées le {{ formatDate(lastUpdated) }}. Aucun appel fournisseur ni modification de données depuis cette vue.
    </p>
  </section>
</template>

<script>
import config from '@/config'
import monitoring, { formatNumber } from '@/views/Stay/monitoring'
import { statusColor, coverageOptions } from './labels'

const statuses = { upcoming: "Départ aujourd'hui ou à venir", current: 'Séjour commencé', past: 'Séjour terminé', undated: 'Dates à vérifier' }
const issues = { missing_tokens: 'TAC ou SPEC absent', missing_accommodation: 'Hébergement absent ou mauvais camping',
  missing_camping: 'Camping absent', missing_source: 'Identifiant SH absent', invalid_period: 'Dates absentes ou invalides',
  invalid_price: 'Prix absents ou incohérents', duplicate: 'Identité en doublon' }

export default {
  mixins: [monitoring],
  data: () => ({
    inventory: null, offers: [], campings: [], offerTotal: 0, campingTotal: 0, tab: 0,
    offerPage: 1, campingPage: 1, search: '', campingSearch: '', selectedCamping: null,
    status: 'all', issue: 'all', publication: 'all', coverage: 'all', coverageOptions,
    statusOptions: [{ text: 'Tous les séjours', value: 'all' }, ...Object.entries(statuses).map(([value, text]) => ({ text, value }))],
    issueOptions: [{ text: 'Tous les contrôles', value: 'all' }, { text: 'Au moins un signal', value: 'issues' },
      ...Object.entries(issues).map(([value, text]) => ({ text, value }))],
    publicationOptions: [{ text: 'Toutes les échéances', value: 'all' }, { text: 'Échéance dépassée', value: 'expired' },
      { text: 'Échéance non dépassée ou absente', value: 'not_expired' }, { text: 'Sans échéance renseignée', value: 'without_deadline' }],
    offerHeaders: [{ text: 'Offre DMDS', value: 'name' }, { text: 'Camping', value: 'campingName' },
      { text: 'Hébergement', value: 'accommodationName' }, { text: 'Séjour', value: 'start' },
      { text: 'Prix promo enregistré', value: 'promoPrice', align: 'end' }, { text: 'Points de contrôle', value: 'issues' }],
    campingHeaders: [{ text: 'Camping', value: 'name' }, { text: 'Séjours en base', value: 'stayCount', align: 'end' },
      { text: "Départs aujourd'hui / à venir", value: 'upcomingCount', align: 'end' }, { text: 'Commencés', value: 'currentCount', align: 'end' },
      { text: 'Terminés', value: 'pastCount', align: 'end' }, { text: 'À vérifier', value: 'issueCount', align: 'end' }, { text: '', value: 'actions' }]
  }),
  computed: {
    metrics () {
      const s = this.inventory || {}

      return [{ label: 'Séjours DMDS en base', value: s.stayCount, hint: 'Source GetLastMinuteOffers uniquement' },
        { label: "Départs aujourd'hui ou à venir", value: s.upcomingCount, hint: 'Selon les dates, pas une disponibilité garantie' },
        { label: 'Campings avec séjours DMDS', value: s.campingCount, hint: this.inventory ? `${formatNumber(s.campingsWithoutOffers)} sans séjour sur ${formatNumber(s.configuredCampingCount)} suivis` : '' },
        { label: 'Séjours avec un point de contrôle', value: s.issueCount, hint: 'Une seule fois par ligne, même avec plusieurs signaux' }]
    },
    qualityMetrics () {
      const s = this.inventory || {}

      return [{ label: 'TAC et SPEC présents', value: s.stayCount - s.missingTokensCount },
        { label: 'TAC ou SPEC absent', value: s.missingTokensCount }, { label: 'Sans hébergement du bon camping', value: s.missingAccommodationCount },
        { label: 'Sans camping connu', value: s.missingCampingCount }, { label: 'Sans identifiant fournisseur', value: s.missingSourceCount },
        { label: 'Dates absentes ou invalides', value: s.invalidPeriodCount }, { label: 'Prix absents ou incohérents', value: s.invalidPriceCount },
        { label: 'Identités en doublon', value: s.duplicateCount }]
    }
  },
  methods: {
    stayStatusLabel: value => statuses[value] || statuses.undated,
    stayStatusColor: statusColor,
    issueLabel: value => issues[value] || value,
    formatAmount (value) {
      return value === null || value === undefined || !Number.isFinite(Number(value)) ? '—'
        : new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value))
    },
    getMonitoring (path, cancelToken, params = {}) {
      return this.$http.get(config.apiUrl + '/api/special-offer/last-minute/' + path, {
        params, cancelToken, headers: { Authorization: 'Bearer ' + this.$store.getters.getToken }
      }).then(response => response.data)
    },
    async loadData (token) {
      const [summary, campings, offers] = await Promise.all([
        this.getMonitoring('summary', token),
        this.getMonitoring('campings', token, { page: this.campingPage, limit: 25, search: this.campingSearch || '', coverage: this.coverage }),
        this.getMonitoring('offers', token, { page: this.offerPage, limit: 25, search: this.search || '', status: this.status,
          issue: this.issue, publication: this.publication, campingId: this.selectedCamping ? this.selectedCamping.id : undefined })
      ])

      return { inventory: summary.inventory, campings: campings.items, campingTotal: campings.total, offers: offers.items, offerTotal: offers.total }
    },
    filtersChanged (list) {
      if (list === 'campings') this.campingPage = 1
      else this.offerPage = 1
      this.refresh()
    },
    resetOfferFilters () {
      this.search = ''
      this.status = 'all'
      this.issue = 'all'
      this.publication = 'all'
      this.selectedCamping = null
    },
    showCamping (camping) {
      this.resetOfferFilters()
      this.selectedCamping = camping
      this.tab = 0
      this.filtersChanged('offers')
    },
    clearCamping () {
      this.selectedCamping = null
      this.filtersChanged('offers')
    },
    resetOffers () {
      this.resetOfferFilters()
      this.filtersChanged('offers')
    }
  }
}
</script>
